import React, { useState, useEffect, useRef, useMemo } from 'react';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  FormController,
  FormLabel,
  Dropdown,
  MenuItem,
  TextField,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
} from '@one-thd/sui-atomic-components';
import {
  DatePicker
} from '@one-thd/sui-date-pickers';
import {
  validateField,
  validateEstimatedStartDate,
  validateEstimatedEndDate,
} from '../helpers';
import {
  PROJECT_NAME_KEY,
  PROJECT_TYPE_KEY,
  ESTIMATED_START_DATE_KEY,
  ESTIMATED_END_DATE_KEY,
  JOB_SITE_CONDITIONS_KEY,
  DELIVERY_INSTRUCTIONS_KEY,
  DELIVERY_CONTACT_NAME_KEY,
  DELIVERY_CONTACT_PHONE_KEY,
  SECURITY_CODE,
  LOCKBOX_CODE,
  LOCKBOX_LOCATION,
  JOB_SITE_HOURS_START,
  JOB_SITE_HOURS_END,
  PROPERTY_TYPE_KEY,
  PROPERTY_TYPE_OTHER_KEY,
  PACKAGE_PLACEMENT_KEY,
  DELIVERY_TYPE_KEY,
  HAS_STAIRS_KEY,
  EMPTY_PROJECT_NAME_MESSAGE,
} from '../constants';
import { JobsiteInformationForm } from './JobsiteInformationForm';

const { bool, func, string, shape } = proptypes;

export const ProjectInformationForm = ({
  showErrors,
  projectInformation,
  setProjectInformation,
  isProjectInfoError,
  setIsProjectInfoError,
  deliveryPreferences,
  setDeliveryPreferences,
  contacts,
  setContacts,
  open,
  duplicateNameError,
  hasTradeCreditAttached,
  jobsiteInformation,
  setJobsiteInformation,
  isJobsiteError,
  setIsJobsiteError,
  hideDeliveryInfo,
  hideProjectInfo
}) => {
  // All refs
  const projectNameRef = useRef(null);
  const projectTypeRef = useRef(null);
  const estimatedEndDateRef = useRef(null);
  const notesRef = useRef(null);
  const siteConditionsRef = useRef(null);
  const deliveryInstructionsRef = useRef(null);
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const packagePlacementRef = useRef(null);
  const securityCodeRef = useRef(null);
  const lockboxCodeRef = useRef(null);
  const lockboxLocationRef = useRef(null);
  const propertyTypeRef = useRef(null);
  const propertyTypeOtherRef = useRef(null);
  const deliveryTypeRef = useRef(null);
  const jobSiteHoursStartRef = useRef(null);
  const jobSiteHoursEndRef = useRef(null);
  const hasStairsErrorRef = useRef(null);

  const [errors, setErrors] = useState({
    projectNameError: '',
    propertyTypeError: '',
    propertyTypeOtherError: '',
    estimatedEndDateError: '',
    notesError: '',
    siteConditionsError: '',
    deliveryInstructionsError: '',
    phoneError: '',
    nameError: '',
    preferredPackagePlacementError: '',
    securityCodeError: '',
    lockboxCodeError: '',
    lockboxLocationError: '',
    deliveryTypeError: '',
    jobSiteHoursStartError: '',
    jobSiteHoursEndError: '',
    hasStairsError: '',
  });

  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const {
    projectName = '',
    projectType = '',
    estimatedStartDate = '',
    estimatedEndDate = '',
    notes = ''
  } = projectInformation || {};

  const {
    siteConditions = '',
    deliveryInstructions = '',
    preferredPackagePlacement = '',
    securityCode = '',
    lockboxCode = '',
    lockboxLocation = '',
    deliveryType = '',
    propertyType = '',
    propertyTypeOther = '',
    hasStairs = null,
    jobSiteHoursStart = '',
    jobSiteHoursEnd = ''
  } = deliveryPreferences || {};

  const definedPropertyTypes = ['House', 'Apartment', 'Business', 'Construction Site'];
  const isPropertyTypeOther = !definedPropertyTypes.includes(propertyType) && !!propertyType;

  const { name = '', phone = '' } = contacts || {};

  const [propertyTypeDropdownValue, setPropertyTypeDropdownValue] = useState('');

  const timeOptions = useMemo(() => {

    return Array.from({ length: 24 }, (_outerScope, hour) => {
      return Array.from({ length: 2 }, (_innerScope, minuteIndex) => {
        const minute = minuteIndex * 30;
        const displayHour = hour.toString().padStart(2, '0');
        const displayMinute = minute.toString().padStart(2, '0');
        return `${displayHour}:${displayMinute}`;
      });
    }).flat();
  }, []);
  const getPhoneNumberDisplayValue = (phoneNumber) => {
    if (!phoneNumber) return '';
    const digits = (phoneNumber || '').replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const getHasStairsDisplayValue = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
  };

  const createValidationHandler = ({ valueGetter, key, errorKey }) => {
    return () => {
      const value = valueGetter();
      const { isError, errorMessage } = validateField({ value, key });
      setErrors((prevState) => ({
        ...prevState,
        [errorKey]: isError ? errorMessage : ''
      }));
    };
  };

  const handleNameValidation = createValidationHandler({
    valueGetter: () => projectName,
    key: PROJECT_NAME_KEY,
    errorKey: 'projectNameError'
  });

  const handleOtherTypeValidation = createValidationHandler({
    valueGetter: () => projectType,
    key: PROJECT_TYPE_KEY,
    errorKey: 'projectTypeError'
  });

  const handleSiteConditionsValidation = createValidationHandler({
    valueGetter: () => siteConditions,
    key: JOB_SITE_CONDITIONS_KEY,
    errorKey: 'siteConditionsError'
  });

  const handleDeliveryInstructionsValidation = createValidationHandler({
    valueGetter: () => deliveryInstructions,
    key: DELIVERY_INSTRUCTIONS_KEY,
    errorKey: 'deliveryInstructionsError'
  });

  const handleContactNameValidation = createValidationHandler({
    valueGetter: () => name,
    key: DELIVERY_CONTACT_NAME_KEY,
    errorKey: 'nameError'
  });

  const handlePhoneValidation = createValidationHandler({
    valueGetter: () => phone,
    key: DELIVERY_CONTACT_PHONE_KEY,
    errorKey: 'phoneError'
  });

  const handleSecurityCodeValidation = createValidationHandler({
    valueGetter: () => securityCode,
    key: SECURITY_CODE,
    errorKey: 'securityCodeError'
  });

  const handleLockBoxCodeValidation = createValidationHandler({
    valueGetter: () => lockboxCode,
    key: LOCKBOX_CODE,
    errorKey: 'lockboxCodeError'
  });

  const handleLockBoxLocationValidation = createValidationHandler({
    valueGetter: () => lockboxLocation,
    key: LOCKBOX_LOCATION,
    errorKey: 'lockboxLocationError'
  });

  const handlePropertyTypeValidation = createValidationHandler({
    valueGetter: () => propertyType,
    key: PROPERTY_TYPE_KEY,
    errorKey: 'propertyTypeError'
  });

  const handlePropertyTypeOtherValidation = createValidationHandler({
    valueGetter: () => propertyTypeOther,
    key: PROPERTY_TYPE_OTHER_KEY,
    errorKey: 'propertyTypeOtherError'
  });

  const handlePackageValidation = createValidationHandler({
    valueGetter: () => preferredPackagePlacement,
    key: PACKAGE_PLACEMENT_KEY,
    errorKey: 'preferredPackagePlacementError'
  });

  const handleDeliveryTypeValidation = createValidationHandler({
    valueGetter: () => deliveryType,
    key: DELIVERY_TYPE_KEY,
    errorKey: 'deliveryTypeError'
  });

  const handleHasStairsValidation = createValidationHandler({
    valueGetter: () => hasStairs,
    key: HAS_STAIRS_KEY,
    errorKey: 'hasStairsError'
  });

  const handleJobSiteHoursStartValidation = createValidationHandler({
    valueGetter: () => jobSiteHoursStart,
    key: JOB_SITE_HOURS_START,
    errorKey: 'jobSiteHoursStartError'
  });

  const handleJobSiteHoursEndValidation = createValidationHandler({
    valueGetter: () => jobSiteHoursEnd,
    key: JOB_SITE_HOURS_END,
    errorKey: 'jobSiteHoursEndError'
  });

  const handleTypeValidation = () => {
    setErrors((prevState) => ({
      ...prevState,
      propertyTypeError: ''
    }));
  };

  const handleChangeAccordion = (event, isExpanded) => {
    setExpandedAccordion(isExpanded);
  };

  const handleEstimatedStartDateValidation = (value) => {
    const { isError, endDateErrorMessage } = validateEstimatedStartDate(value, estimatedEndDate);
    setErrors((prevState) => ({
      ...prevState,
      estimatedEndDateError: isError ? endDateErrorMessage : ''
    }));
  };

  const handleEstimatedEndDateValidation = (value) => {
    const { isError, endDateErrorMessage } = validateEstimatedEndDate(value, estimatedStartDate);
    setErrors((prevState) => ({
      ...prevState,
      estimatedEndDateError: isError ? endDateErrorMessage : ''
    }));
  };

  const handleOnDateChange = (key, event, value) => {
    let formattedValue = value;
    if (value) {
      formattedValue = new Date(value).toLocaleDateString(
        'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }
      );
    }
    if (key === ESTIMATED_START_DATE_KEY) handleEstimatedStartDateValidation(formattedValue);
    if (key === ESTIMATED_END_DATE_KEY) handleEstimatedEndDateValidation(formattedValue);
    setProjectInformation((prevState) => ({
      ...prevState,
      [key]: formattedValue
    }));
  };

  const handleOnChange = (key, event) => {
    const { value } = event.target;
    if (key === DELIVERY_CONTACT_NAME_KEY) {
      setContacts((prevState) => ({
        ...prevState,
        [key]: value
      }));
    } else if (key === DELIVERY_CONTACT_PHONE_KEY) {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setContacts((prevState) => ({
        ...prevState,
        [key]: digits
      }));
    } else {
      setProjectInformation((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  };

  const handleDeliveryPreferencesOnChange = (key, event) => {
    const { value } = event.target;
    let processedValue = value;
    if (key === HAS_STAIRS_KEY) {
      if (value === 'Yes') {
        processedValue = true;
      } else if (value === 'No') {
        processedValue = false;
      } else {
        processedValue = null;
      }
    }
    let isPropertyType = key === PROPERTY_TYPE_KEY;
    if (isPropertyType) {
      if (value === 'Other') {
        handlePropertyTypeOtherValidation();
      } else {
        setErrors((prevSate) => ({
          ...prevSate,
          propertyTypeOtherError: ''
        }));
      }
    }
    setDeliveryPreferences((prevState) => ({
      ...prevState,
      [key]: processedValue,
      ...(isPropertyType ? {[PROPERTY_TYPE_OTHER_KEY]: '' } : {})
    }));
  };

  const handleOnPropertyChange = (key, event) => {
    const { value } = event.target;
    setPropertyTypeDropdownValue(value);
    if (value === 'Other') {
      setProjectInformation((prevState) => ({
        ...prevState,
        projectType: ''
      }));
    } else {
      setProjectInformation((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  };

  useEffect(() => {
    const errorMessageValues = Object.values(errors);
    const hasErrors = errorMessageValues.some(Boolean);
    setIsProjectInfoError(hasErrors);
  }, [errors, setIsProjectInfoError]);

  useEffect(() => {
    const findFirstTruthyKey = (obj) => {
      return Object.keys(obj).find((key) => obj[key]) || null;
    };
    const firstErrorKey = findFirstTruthyKey(errors);
    if (showErrors && firstErrorKey) {
      const errorRefMap = {
        projectNameError: projectNameRef,
        projectTypeError: projectTypeRef,
        estimatedEndDateError: estimatedEndDateRef,
        notesError: notesRef,
        siteConditionsError: siteConditionsRef,
        deliveryInstructionsError: deliveryInstructionsRef,
        nameError: nameRef,
        phoneError: phoneRef,
        preferredPackagePlacementError: packagePlacementRef,
        securityCodeError: securityCodeRef,
        lockboxCodeError: lockboxCodeRef,
        lockboxLocationError: lockboxLocationRef,
        deliveryTypeError: deliveryTypeRef,
        jobSiteHoursStartError: jobSiteHoursStartRef,
        jobSiteHoursEndError: jobSiteHoursEndRef,
        propertyTypeError: propertyTypeRef,
        hasStairsError: hasStairsErrorRef,
      };
      const targetRef = errorRefMap[firstErrorKey];
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    }
  }, [showErrors, errors]);

  useEffect(() => {
    if (showErrors && !projectName) {
      setErrors((prevSate) => ({
        ...prevSate,
        projectNameError: EMPTY_PROJECT_NAME_MESSAGE
      }));
    }
  }, [showErrors]);

  useEffect(() => {
    if (projectType) {
      // eslint-disable-next-line max-len
      if (projectType !== 'Single Family' && projectType !== 'Multi-Family' && projectType !== 'Renovation/Remodel') {
        setPropertyTypeDropdownValue('Other');
      } else {
        setPropertyTypeDropdownValue(projectType);
      }
    }
  }, []);

  const renderFormFields = (isAccordion = false) => {
    const idSuffix = isAccordion ? '-accordion' : '';
    const testIdSuffix = isAccordion ? '-accordion' : '';

    if (isAccordion) {
      return (
        <>
          {!hideProjectInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`project-type-form-dropdown${idSuffix}`}
                id={`project-type-form-label${idSuffix}`}
              >
                Project Type
              </FormLabel>
              <Dropdown
                maxItems={4}
                id={`project-type-form-dropdown${idSuffix}`}
                data-testid="project-type-dropdown"
                value={propertyTypeDropdownValue || ''}
                onBlur={() => { handleTypeValidation(); }}
                onChange={(event) => { handleOnPropertyChange(PROJECT_TYPE_KEY, event); }}
                placeholder="Select a Project Type"
              >
                <MenuItem value="Single Family" data-testid="single-family">Single Family</MenuItem>
                <MenuItem value="Multi-Family" data-testid="multi-family">Multi-Family</MenuItem>
                <MenuItem value="Renovation/Remodel" data-testid="renovation-remodel">Renovation/Remodel</MenuItem>
                <MenuItem value="Other" data-testid="type-other">Other</MenuItem>
              </Dropdown>
              {propertyTypeDropdownValue === 'Other' && (
                <TextField
                  fullWidth
                  ref={projectTypeRef}
                  id="project-type"
                  placeholder="Project Type"
                  value={projectType || ''}
                  onBlur={handleOtherTypeValidation}
                  onChange={(event) => { handleOnChange(PROJECT_TYPE_KEY, event); }}
                  status={showErrors && errors.projectTypeError ? 'error' : null}
                  statusMessage={showErrors ? errors.projectTypeError : ''}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'project-type-input'
                    }
                  }}
                />
              )}
            </FormController>
          )}

          {!hideProjectInfo && (
            <div className="sui-grid sui-gap-2">
              <DatePicker
                dateFieldProps={{
                  label: 'Estimated Start Date',
                  optional: 'true'
                }}
                collapsible
                value={estimatedStartDate || ''}
                onChange={(event, value) => { handleOnDateChange(ESTIMATED_START_DATE_KEY, event, value); }}
                id="project-start-date"
                data-testid="project-start-date-input"
              />
              <DatePicker
                ref={estimatedEndDateRef}
                dateFieldProps={{
                  label: 'Estimated End Date',
                  optional: 'true'
                }}
                collapsible
                value={estimatedEndDate || ''}
                onChange={(event, value) => { handleOnDateChange(ESTIMATED_END_DATE_KEY, event, value); }}
                id="project-end-date"
                data-testid="project-end-date-input"
                status={showErrors && errors.estimatedEndDateError ? 'error' : null}
                statusMessage={showErrors ? errors.estimatedEndDateError : ''}
              />
            </div>
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={siteConditionsRef}
              optional
              id="jobsite-conditions"
              label="Jobsite Conditions"
              placeholder=""
              value={siteConditions || ''}
              onBlur={handleSiteConditionsValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_CONDITIONS_KEY, event); }}
              status={showErrors && errors.siteConditionsError ? 'error' : null}
              statusMessage={showErrors ? errors.siteConditionsError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'jobsite-conditions-input'
                }
              }}
            />
          )}

          {!hideProjectInfo && (
            <TextField
              fullWidth
              ref={nameRef}
              optional
              id="delivery-contact-name"
              label="Delivery Contact Name"
              placeholder=""
              value={name || ''}
              onBlur={handleContactNameValidation}
              onChange={(event) => { handleOnChange('name', event); }}
              status={showErrors && errors.nameError ? 'error' : null}
              statusMessage={showErrors ? errors.nameError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-contact-name-input'
                }
              }}
            />
          )}

          {!hideProjectInfo && (
            <TextField
              fullWidth
              ref={phoneRef}
              optional
              id="delivery-contact-number"
              label="Delivery Contact Number"
              placeholder="(555) 555-5555"
              value={getPhoneNumberDisplayValue(phone) || ''}
              onBlur={handlePhoneValidation}
              onChange={(event) => { handleOnChange('phone', event); }}
              status={showErrors && errors.phoneError ? 'error' : null}
              statusMessage={showErrors ? errors.phoneError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-contact-number-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={deliveryInstructionsRef}
              optional
              id="delivery-instructions"
              label="Delivery Instructions"
              placeholder=""
              value={deliveryInstructions || ''}
              onBlur={handleDeliveryInstructionsValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(DELIVERY_INSTRUCTIONS_KEY, event); }}
              status={showErrors && errors.deliveryInstructionsError ? 'error' : null}
              statusMessage={showErrors ? errors.deliveryInstructionsError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-instructions-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`delivery-type${idSuffix}`}
                id={`delivery-type${idSuffix}-label`}
              >
                Delivery Type
              </FormLabel>
              <Dropdown
                ref={deliveryTypeRef}
                id={`delivery-type${idSuffix}`}
                data-testid={`delivery-type${testIdSuffix}-dropdown`}
                value={deliveryType || ''}
                onBlur={handleDeliveryTypeValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(DELIVERY_TYPE_KEY, event); }}
                placeholder="Select a delivery type"
                status={showErrors && errors.deliveryTypeError ? 'error' : null}
                statusMessage={showErrors ? errors.deliveryTypeError : ''}
              >
                <MenuItem value="Outside" data-testid={`delivery${testIdSuffix}-outside`}>Outside</MenuItem>
                <MenuItem value="Threshold" data-testid={`delivery${testIdSuffix}-threshold`}>Threshold</MenuItem>
                <MenuItem value="Lockbox" data-testid={`delivery${testIdSuffix}-lockbox`}>Lockbox</MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`package-placement${idSuffix}`}
                id={`package-placement${idSuffix}-label`}
              >
                Package Placement
              </FormLabel>
              <Dropdown
                ref={packagePlacementRef}
                id={`package-placement${idSuffix}`}
                data-testid={`package-placement${testIdSuffix}-dropdown`}
                value={preferredPackagePlacement || ''}
                onBlur={handlePackageValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(PACKAGE_PLACEMENT_KEY, event); }}
                placeholder="Select a placement option"
                status={showErrors && errors.preferredPackagePlacementError ? 'error' : null}
                statusMessage={showErrors ? errors.preferredPackagePlacementError : ''}
              >
                <MenuItem value="Front Door" data-testid={`package${testIdSuffix}-front-door`}>Front Door</MenuItem>
                <MenuItem value="Back Door" data-testid={`package${testIdSuffix}-back-door`}>Back Door</MenuItem>
                <MenuItem value="Porch" data-testid={`package${testIdSuffix}-porch`}>Porch</MenuItem>
                <MenuItem value="Office" data-testid={`package${testIdSuffix}-office`}>Office</MenuItem>
                <MenuItem value="Mailroom" data-testid={`package${testIdSuffix}-mailroom`}>Mailroom</MenuItem>
                <MenuItem value="No Preference" data-testid={`package${testIdSuffix}-no-preference`}>
                  No Preference
                </MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={securityCodeRef}
              optional
              id="security-code"
              label="Security Code"
              placeholder=""
              value={securityCode || ''}
              onBlur={handleSecurityCodeValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(SECURITY_CODE, event); }}
              status={showErrors && errors.securityCodeError ? 'error' : null}
              statusMessage={showErrors ? errors.securityCodeError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'security-code-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={lockboxCodeRef}
              optional
              id="lockbox-code"
              label="Lockbox Code"
              placeholder=""
              value={lockboxCode || ''}
              onBlur={handleLockBoxCodeValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(LOCKBOX_CODE, event); }}
              status={showErrors && errors.lockboxCodeError ? 'error' : null}
              statusMessage={showErrors ? errors.lockboxCodeError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'lockbox-code-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={lockboxLocationRef}
              optional
              id="lockbox-location"
              label="Lockbox Location"
              placeholder=""
              value={lockboxLocation || ''}
              onBlur={handleLockBoxLocationValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(LOCKBOX_LOCATION, event); }}
              status={showErrors && errors.lockboxLocationError ? 'error' : null}
              statusMessage={showErrors ? errors.lockboxLocationError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'lockbox-location-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={propertyTypeRef}
              optional
              id="property-type"
              label="Property Type"
              placeholder=""
              value={propertyType || ''}
              onBlur={handlePropertyTypeValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(PROPERTY_TYPE_KEY, event); }}
              status={showErrors && errors.propertyTypeError ? 'error' : null}
              statusMessage={showErrors ? errors.propertyTypeError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-type-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`has-stairs${idSuffix}`}
                id={`has-stairs${idSuffix}-label`}
              >
                Has Stairs?
              </FormLabel>

              <Dropdown
                ref={hasStairsErrorRef}
                id={`has-stairs${idSuffix}`}
                data-testid={`has-stairs${testIdSuffix}-dropdown`}
                value={getHasStairsDisplayValue(hasStairs)}
                onBlur={handleHasStairsValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(HAS_STAIRS_KEY, event); }}
                placeholder="Select an option"
                status={showErrors && errors.hasStairsError ? 'error' : null}
                statusMessage={showErrors ? errors.hasStairsError : ''}
              >
                <MenuItem value="Yes" data-testid={`has-stairs${testIdSuffix}-yes`}>Yes</MenuItem>
                <MenuItem value="No" data-testid={`has-stairs${testIdSuffix}-no`}>No</MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`job-site-hours-start${idSuffix}`}
                id={`job-site-hours-start${idSuffix}-label`}
              >
                Job Site Hours Start
              </FormLabel>
              <Dropdown
                ref={jobSiteHoursStartRef}
                id={`job-site-hours-start${idSuffix}`}
                data-testid={`job-site-hours-start${testIdSuffix}-dropdown`}
                value={jobSiteHoursStart || ''}
                onBlur={handleJobSiteHoursStartValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_HOURS_START, event); }}
                placeholder="Select start time"
                status={showErrors && errors.jobSiteHoursStartError ? 'error' : null}
                statusMessage={showErrors ? errors.jobSiteHoursStartError : ''}
              >
                {timeOptions.map((time) => (
                  <MenuItem
                    key={time}
                    value={time}
                    data-testid={`start-time${testIdSuffix}-${time.replace(/[:\s]/g, '-')}`}
                  >
                    {time}
                  </MenuItem>
                ))}
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`job-site-hours-end${idSuffix}`}
                id={`job-site-hours-end${idSuffix}-label`}
              >
                Job Site Hours End
              </FormLabel>
              <Dropdown
                ref={jobSiteHoursEndRef}
                id={`job-site-hours-end${idSuffix}`}
                data-testid={`job-site-hours-end${testIdSuffix}-dropdown`}
                value={jobSiteHoursEnd || ''}
                onBlur={handleJobSiteHoursEndValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_HOURS_END, event); }}
                placeholder="Select end time"
                status={showErrors && errors.jobSiteHoursEndError ? 'error' : null}
                statusMessage={showErrors ? errors.jobSiteHoursEndError : ''}
              >
                {timeOptions.map((time) => (
                  <MenuItem
                    key={time}
                    value={time}
                    data-testid={`end-time${testIdSuffix}-${time.replace(/[:\s]/g, '-')}`}
                  >
                    {time}
                  </MenuItem>
                ))}
              </Dropdown>
            </FormController>
          )}
        </>
      );
      /* eslint-disable no-else-return */
    } else {
      // Non-accordion order:
      // 1. Project Type
      // 2. Estimated Start Date
      // 3. Estimated End Date
      // 4. Delivery Contact Name
      // 5. Delivery Contact Number
      // 6. Jobsite Conditions
      // 7. Delivery Instructions
      // 8. Package Placement
      // 9. Security Code
      // 10. Lockbox Code
      // 11. Lockbox Location
      // 12. Property Type
      // 13. Delivery Type
      // 14. Has Stairs?
      // 15. Job Site Hours Start
      // 16. Job Site Hours End
      return (
        <>
          {!hideProjectInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`project-type-form-dropdown${idSuffix}`}
                id={`project-type-form-label${idSuffix}`}
              >
                Project Type
              </FormLabel>
              <Dropdown
                maxItems={4}
                id={`project-type-form-dropdown${idSuffix}`}
                data-testid="project-type-dropdown"
                value={propertyTypeDropdownValue || ''}
                onBlur={() => { handleTypeValidation(); }}
                onChange={(event) => { handleOnPropertyChange(PROJECT_TYPE_KEY, event); }}
                placeholder="Select a Project Type"
              >
                <MenuItem value="Single Family" data-testid="single-family">Single Family</MenuItem>
                <MenuItem value="Multi-Family" data-testid="multi-family">Multi-Family</MenuItem>
                <MenuItem value="Renovation/Remodel" data-testid="renovation-remodel">Renovation/Remodel</MenuItem>
                <MenuItem value="Other" data-testid="type-other">Other</MenuItem>
              </Dropdown>
              {propertyTypeDropdownValue === 'Other' && (
                <TextField
                  fullWidth
                  ref={projectTypeRef}
                  id="project-type"
                  placeholder="Project Type"
                  value={projectType || ''}
                  onBlur={handleOtherTypeValidation}
                  onChange={(event) => { handleOnChange(PROJECT_TYPE_KEY, event); }}
                  status={showErrors && errors.projectTypeError ? 'error' : null}
                  statusMessage={showErrors ? errors.projectTypeError : ''}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'project-type-input'
                    }
                  }}
                />
              )}
            </FormController>
          )}

          {!hideProjectInfo && (
            <div>
              <div className="sui-mb-6">
                <DatePicker
                  dateFieldProps={{
                    label: 'Estimated Start Date',
                    optional: 'true'
                  }}
                  collapsible
                  value={estimatedStartDate || ''}
                  onChange={(event, value) => { handleOnDateChange(ESTIMATED_START_DATE_KEY, event, value); }}
                  id="project-start-date"
                  data-testid="project-start-date-input"
                />
              </div>
              <div className="sui-mb-6">
                <DatePicker
                  ref={estimatedEndDateRef}
                  dateFieldProps={{
                    label: 'Estimated End Date',
                    optional: 'true'
                  }}
                  collapsible
                  value={estimatedEndDate || ''}
                  onChange={(event, value) => { handleOnDateChange(ESTIMATED_END_DATE_KEY, event, value); }}
                  id="project-end-date"
                  data-testid="project-end-date-input"
                  status={showErrors && errors.estimatedEndDateError ? 'error' : null}
                  statusMessage={showErrors ? errors.estimatedEndDateError : ''}
                />
              </div>
            </div>
          )}

          {!hideProjectInfo && (
            <TextField
              fullWidth
              ref={nameRef}
              optional
              id="delivery-contact-name"
              label="Delivery Contact Name"
              placeholder=""
              value={name || ''}
              onBlur={handleContactNameValidation}
              onChange={(event) => { handleOnChange('name', event); }}
              status={showErrors && errors.nameError ? 'error' : null}
              statusMessage={showErrors ? errors.nameError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-contact-name-input'
                }
              }}
            />
          )}

          {!hideProjectInfo && (
            <TextField
              fullWidth
              ref={phoneRef}
              optional
              id="delivery-contact-number"
              label="Delivery Contact Number"
              placeholder="(555) 555-5555"
              value={getPhoneNumberDisplayValue(phone) || ''}
              onBlur={handlePhoneValidation}
              onChange={(event) => { handleOnChange('phone', event); }}
              status={showErrors && errors.phoneError ? 'error' : null}
              statusMessage={showErrors ? errors.phoneError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-contact-number-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={siteConditionsRef}
              optional
              id="jobsite-conditions"
              label="Jobsite Conditions"
              placeholder=""
              value={siteConditions || ''}
              onBlur={handleSiteConditionsValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_CONDITIONS_KEY, event); }}
              status={showErrors && errors.siteConditionsError ? 'error' : null}
              statusMessage={showErrors ? errors.siteConditionsError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'jobsite-conditions-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={deliveryInstructionsRef}
              optional
              id="delivery-instructions"
              label="Delivery Instructions"
              placeholder=""
              value={deliveryInstructions || ''}
              onBlur={handleDeliveryInstructionsValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(DELIVERY_INSTRUCTIONS_KEY, event); }}
              status={showErrors && errors.deliveryInstructionsError ? 'error' : null}
              statusMessage={showErrors ? errors.deliveryInstructionsError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'delivery-instructions-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`package-placement${idSuffix}`}
                id={`package-placement${idSuffix}-label`}
              >
                Package Placement
              </FormLabel>
              <Dropdown
                ref={packagePlacementRef}
                id={`package-placement${idSuffix}`}
                data-testid={`package-placement${testIdSuffix}-dropdown`}
                value={preferredPackagePlacement || ''}
                onBlur={handlePackageValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(PACKAGE_PLACEMENT_KEY, event); }}
                placeholder="Select a placement option"
                status={showErrors && errors.preferredPackagePlacementError ? 'error' : null}
                statusMessage={showErrors ? errors.preferredPackagePlacementError : ''}
              >
                <MenuItem value="Front Door" data-testid={`package${testIdSuffix}-front-door`}>Front Door</MenuItem>
                <MenuItem value="Back Door" data-testid={`package${testIdSuffix}-back-door`}>Back Door</MenuItem>
                <MenuItem value="Porch" data-testid={`package${testIdSuffix}-porch`}>Porch</MenuItem>
                <MenuItem value="Office" data-testid={`package${testIdSuffix}-office`}>Office</MenuItem>
                <MenuItem value="Mailroom" data-testid={`package${testIdSuffix}-mailroom`}>Mailroom</MenuItem>
                <MenuItem value="No Preference" data-testid={`package${testIdSuffix}-no-preference`}>
                  No Preference
                </MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={securityCodeRef}
              optional
              id="security-code"
              label="Security Code"
              placeholder=""
              value={securityCode || ''}
              onBlur={handleSecurityCodeValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(SECURITY_CODE, event); }}
              status={showErrors && errors.securityCodeError ? 'error' : null}
              statusMessage={showErrors ? errors.securityCodeError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'security-code-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={lockboxCodeRef}
              optional
              id="lockbox-code"
              label="Lockbox Code"
              placeholder=""
              value={lockboxCode || ''}
              onBlur={handleLockBoxCodeValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(LOCKBOX_CODE, event); }}
              status={showErrors && errors.lockboxCodeError ? 'error' : null}
              statusMessage={showErrors ? errors.lockboxCodeError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'lockbox-code-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <TextField
              fullWidth
              ref={lockboxLocationRef}
              optional
              id="lockbox-location"
              label="Lockbox Location"
              placeholder=""
              value={lockboxLocation || ''}
              onBlur={handleLockBoxLocationValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(LOCKBOX_LOCATION, event); }}
              status={showErrors && errors.lockboxLocationError ? 'error' : null}
              statusMessage={showErrors ? errors.lockboxLocationError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'lockbox-location-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`property-type${idSuffix}`}
                id={`property-type${idSuffix}-label`}
              >
                Property Type
              </FormLabel>
              <Dropdown
                ref={propertyTypeRef}
                id={`property-type${idSuffix}`}
                data-testid={`property-type${testIdSuffix}-dropdown`}
                value={isPropertyTypeOther ? 'Other' : propertyType || ''}
                onBlur={handlePropertyTypeValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(PROPERTY_TYPE_KEY, event); }}
                placeholder="Select a property type"
                status={showErrors && errors.propertyTypeError ? 'error' : null}
                statusMessage={showErrors ? errors.propertyTypeError : ''}
              >
                <MenuItem value="House" data-testid={`property-type${testIdSuffix}-house`}>House</MenuItem>
                <MenuItem value="Apartment" data-testid={`property-type${testIdSuffix}-apartment`}>Apartment</MenuItem>
                <MenuItem value="Business" data-testid={`property-type${testIdSuffix}-business`}>Business</MenuItem>
                <MenuItem value="Construction Site" data-testid={`property-type${testIdSuffix}-construction-site`}>Construction Site</MenuItem>
                <MenuItem value="Other" data-testid={`property-type${testIdSuffix}-other`}>
                  Other
                </MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && isPropertyTypeOther && (
            <TextField
              fullWidth
              ref={propertyTypeOtherRef}
              required
              id="property-type-other"
              label="Describe property type"
              placeholder=""
              value={propertyTypeOther || ''}
              onBlur={handlePropertyTypeOtherValidation}
              onChange={(event) => { handleDeliveryPreferencesOnChange(PROPERTY_TYPE_OTHER_KEY, event); }}
              status={showErrors && errors.propertyTypeOtherError ? 'error' : null}
              statusMessage={showErrors ? errors.propertyTypeOtherError : ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'property-type-other-input'
                }
              }}
            />
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`delivery-type${idSuffix}`}
                id={`delivery-type${idSuffix}-label`}
              >
                Delivery Type
              </FormLabel>
              <Dropdown
                ref={deliveryTypeRef}
                id={`delivery-type${idSuffix}`}
                data-testid={`delivery-type${testIdSuffix}-dropdown`}
                value={deliveryType || ''}
                onBlur={handleDeliveryTypeValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(DELIVERY_TYPE_KEY, event); }}
                placeholder="Select a delivery type"
                status={showErrors && errors.deliveryTypeError ? 'error' : null}
                statusMessage={showErrors ? errors.deliveryTypeError : ''}
              >
                <MenuItem value="Outside" data-testid={`delivery${testIdSuffix}-outside`}>Outside</MenuItem>
                <MenuItem value="Threshold" data-testid={`delivery${testIdSuffix}-threshold`}>Threshold</MenuItem>
                <MenuItem value="Lockbox" data-testid={`delivery${testIdSuffix}-lockbox`}>Lockbox</MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`has-stairs${idSuffix}`}
                id={`has-stairs${idSuffix}-label`}
              >
                Has Stairs?
              </FormLabel>
              <Dropdown
                ref={hasStairsErrorRef}
                id={`has-stairs${idSuffix}`}
                data-testid={`has-stairs${testIdSuffix}-dropdown`}
                value={getHasStairsDisplayValue(hasStairs)}
                onBlur={handleHasStairsValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(HAS_STAIRS_KEY, event); }}
                placeholder="Select an option"
                status={showErrors && errors.hasStairsError ? 'error' : null}
                statusMessage={showErrors ? errors.hasStairsError : ''}
              >
                <MenuItem value="Yes" data-testid={`has-stairs${testIdSuffix}-yes`}>Yes</MenuItem>
                <MenuItem value="No" data-testid={`has-stairs${testIdSuffix}-no`}>No</MenuItem>
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`job-site-hours-start${idSuffix}`}
                id={`job-site-hours-start${idSuffix}-label`}
              >
                Job Site Hours Start
              </FormLabel>
              <Dropdown
                ref={jobSiteHoursStartRef}
                id={`job-site-hours-start${idSuffix}`}
                data-testid={`job-site-hours-start${testIdSuffix}-dropdown`}
                value={jobSiteHoursStart || ''}
                onBlur={handleJobSiteHoursStartValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_HOURS_START, event); }}
                placeholder="Select start time"
                status={showErrors && errors.jobSiteHoursStartError ? 'error' : null}
                statusMessage={showErrors ? errors.jobSiteHoursStartError : ''}
              >
                {timeOptions.map((time) => (
                  <MenuItem
                    key={time}
                    value={time}
                    data-testid={`start-time${testIdSuffix}-${time.replace(/[:\s]/g, '-')}`}
                  >
                    {time}
                  </MenuItem>
                ))}
              </Dropdown>
            </FormController>
          )}

          {!hideDeliveryInfo && (
            <FormController fullWidth>
              <FormLabel
                optional
                htmlFor={`job-site-hours-end${idSuffix}`}
                id={`job-site-hours-end${idSuffix}-label`}
              >
                Job Site Hours End
              </FormLabel>
              <Dropdown
                ref={jobSiteHoursEndRef}
                id={`job-site-hours-end${idSuffix}`}
                data-testid={`job-site-hours-end${testIdSuffix}-dropdown`}
                value={jobSiteHoursEnd || ''}
                onBlur={handleJobSiteHoursEndValidation}
                onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_HOURS_END, event); }}
                placeholder="Select end time"
                status={showErrors && errors.jobSiteHoursEndError ? 'error' : null}
                statusMessage={showErrors ? errors.jobSiteHoursEndError : ''}
              >
                {timeOptions.map((time) => (
                  <MenuItem
                    key={time}
                    value={time}
                    data-testid={`end-time${testIdSuffix}-${time.replace(/[:\s]/g, '-')}`}
                  >
                    {time}
                  </MenuItem>
                ))}
              </Dropdown>
            </FormController>
          )}
        </>
      );
    }
  };

  return (
    <>
      {!hideProjectInfo && (
        <TextField
          required
          ref={projectNameRef}
          id="project-name"
          label="Project Name"
          type="text"
          onBlur={handleNameValidation}
          value={projectName || ''}
          fullWidth
          onChange={(event) => { handleOnChange(PROJECT_NAME_KEY, event); }}
          disabled={hasTradeCreditAttached}
          status={
            errors.projectNameError || duplicateNameError ? 'error' : null
          }
          statusMessage={
            errors.projectNameError || duplicateNameError
          }
          InputProps={{
            inputProps: {
              'data-testid': 'project-name-input'
            }
          }}
        />
      )}

      {open ? (
        <>
          {renderFormFields(false)}
        </>
      ) : (
        <Accordion expanded={expandedAccordion} onChange={handleChangeAccordion}>
          <AccordionHeader aria-controls="accordion1-content" id="accordion1-header">
            <div className="sui-flex-auto sui-block">
              <Typography variant="h6">Project Details (Optional)</Typography>
              <div className="sui-mt-1">
                <Typography variant="body-base" color="subtle">
                  Adding these details will help us show you accurate inventory and delivery times
                </Typography>
              </div>
            </div>
          </AccordionHeader>
          <AccordionBody>
            <div className="sui-grid sui-gap-6">
              {renderFormFields(true)}
              <div>
                <Typography variant="body-lg" weight="bold" className="sui-pt-8 sui-pb-4">
                  Jobsite Information
                </Typography>
                <JobsiteInformationForm
                  showErrors={showErrors}
                  jobsiteInformation={jobsiteInformation}
                  setJobsiteInformation={setJobsiteInformation}
                  isJobsiteError={isJobsiteError}
                  setIsJobsiteError={setIsJobsiteError}
                  open={open}
                />
              </div>
            </div>
          </AccordionBody>
        </Accordion>
      )}
    </>
  );
};

// PropTypes and defaultProps remain the same...
ProjectInformationForm.propTypes = {
  showErrors: bool.isRequired,
  projectInformation: shape({
    projectName: string,
    projectType: string,
    otherPropertyType: string,
    estimatedStartDate: string,
    estimatedEndDate: string,
    notes: string
  }).isRequired,
  deliveryPreferences: shape({
    siteConditions: string,
    deliveryInstructions: string,
    preferredPackagePlacement: string,
    securityCode: string,
    lockboxCode: string,
    lockboxLocation: string,
    deliveryType: string,
    propertyType: string,
    hasStairs: bool,
    jobSiteHoursStart: string,
    jobSiteHoursEnd: string,
  }).isRequired,
  contacts: shape({
    name: string,
    phone: string
  }).isRequired,
  setProjectInformation: func.isRequired,
  isProjectInfoError: bool.isRequired,
  setIsProjectInfoError: func.isRequired,
  open: bool.isRequired,
  duplicateNameError: string.isRequired,
  hasTradeCreditAttached: bool,
  setDeliveryPreferences: func.isRequired,
  setContacts: func.isRequired,
  jobsiteInformation: shape({
    addressLine1: string,
    addressLine2: string,
    city: string,
    state: string,
    zipCode: string,
    country: string,
  }).isRequired,
  setJobsiteInformation: func.isRequired,
  isJobsiteError: bool.isRequired,
  setIsJobsiteError: func.isRequired,
  hideDeliveryInfo: bool,
  hideProjectInfo: bool
};

ProjectInformationForm.defaultProps = {
  hasTradeCreditAttached: false,
  hideDeliveryInfo: false,
  hideProjectInfo: false
};

ProjectInformationForm.dataModel = extend(
  JobsiteInformationForm
);